/** Nav, footer and the phone-only "Get the app" bar shared by home layouts W, X and Y. */
import React, { useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { userContext } from "../../../../../stores/users/userContext";
import { ROUTES } from "../../../../../constants/routes";
import { Wordmark, scrollToId, storeForThisPhone } from "./kit";
import { APP_STORE_URL, NAV, PLAY_STORE_URL, storeLink } from "../../constants/homeLayoutsText";

export { storeForThisPhone };

export type NavLink = { id: string; label: string };

export const useHomeActions = () => {
  const navigate = useNavigate();
  return {
    signIn: () => navigate(ROUTES.LANDING),
    library: () => navigate(ROUTES.MY_LIBRARY),
    pricing: () => navigate(ROUTES.PRICING),
    plugin: () => navigate(ROUTES.PLUGIN),
  };
};

export const H3Nav: React.FC<{ links: NavLink[]; onLight?: boolean; cta?: { label: string; onClick: () => void } }> = observer(({ links, onLight = false, cta }) => {
  const userStore = useContext(userContext);
  const authed = userStore.isUserAuth();
  const act = useHomeActions();
  return (
    <header className="h3-nav" id="h3-top">
      <div className="wrap">
        <button className="brandbtn" type="button" onClick={() => scrollToId("h3-top")} aria-label="CherryClipz home">
          <Wordmark size={30} onLight={onLight} />
        </button>
        <nav className="links" aria-label="Sections">
          {links.map((l) => (
            <button key={l.id} type="button" onClick={() => scrollToId(l.id)}>
              {l.label}
            </button>
          ))}
        </nav>
        <div className="end">
          <button className="signin" type="button" onClick={authed ? act.library : act.signIn}>
            {authed ? NAV.library : NAV.signIn}
          </button>
          <button className="h3-btn cherry sm" type="button" onClick={cta ? cta.onClick : () => scrollToId("h3-get")}>
            {cta ? cta.label : NAV.getApp}
          </button>
        </div>
      </div>
    </header>
  );
});

export const H3Footer: React.FC<{ onLight?: boolean }> = ({ onLight = false }) => {
  const act = useHomeActions();
  return (
    <footer className="h3-foot">
      <div className="wrap">
        <Wordmark size={24} onLight={onLight} />
        <nav aria-label="Footer">
          <button type="button" onClick={() => window.open(APP_STORE_URL, "_blank", "noopener")}>
            App Store
          </button>
          <button type="button" onClick={() => window.open(PLAY_STORE_URL, "_blank", "noopener")}>
            Google Play
          </button>
          <button type="button" onClick={act.plugin}>
            Premiere panel
          </button>
          <button type="button" onClick={act.pricing}>
            {NAV.pricing}
          </button>
          <button type="button" onClick={act.signIn}>
            {NAV.signIn}
          </button>
        </nav>
        <small>© 2026 CherryClipz</small>
      </div>
    </footer>
  );
};

/** A slim bar pinned to the bottom on phones, pointing at the right store. Hidden on desktop by CSS. */
export const GetAppBar: React.FC<{ text: string; tag: string }> = ({ text, tag }) => {
  const href = useMemo(() => storeLink(storeForThisPhone() || APP_STORE_URL, `${tag}-bar`), [tag]);
  return (
    <div className="h3-getbar" role="complementary" aria-label="Get the app">
      <span>{text}</span>
      <a href={href} target="_blank" rel="noreferrer">
        {NAV.getApp}
      </a>
    </div>
  );
};
